import { Injectable } from "@angular/core";
import { OrderTicket } from "@models/order";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

@Injectable({
  providedIn: "root",
})
export class TicketPdfService {
  /**
   * Generates and downloads a PDF for a ticket
   * @param ticket The ticket data to include in the PDF
   * @param qrCodeData The QR code image data URL
   */
  async downloadTicketPdf(ticket: OrderTicket, qrCodeData?: string): Promise<void> {
    try {
      // Create a temporary container for the ticket content
      const container = this.createTicketContainer(ticket, qrCodeData);
      document.body.appendChild(container);

      // Wait a bit for images to load
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Convert the container to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Remove the temporary container
      document.body.removeChild(container);

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF({
        orientation: imgHeight > imgWidth ? "portrait" : "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

      // Download the PDF
      const fileName = `ingresso-${ticket.title.replace(/\s+/g, "-")}-${ticket.id}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  }

  /**
   * Creates a temporary HTML container with the ticket information
   */
  private createTicketContainer(ticket: OrderTicket, qrCodeData?: string): HTMLElement {
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.width = "800px";
    container.style.backgroundColor = "#ffffff";
    container.style.padding = "40px";
    container.style.fontFamily = "Arial, sans-serif";

    const logoUrl = "./assets/images/footer/yes7-ticket-logo-footer.png";

    container.innerHTML = `
      <div style="text-align: center; margin-bottom: 30px;">
        <img src="${logoUrl}" alt="Yes Seven Ticket" style="width: 250px; margin-bottom: 20px;" />
        <h1 style="color: #7c0084; margin: 0; font-size: 28px;">Detalhes do Ingresso</h1>
      </div>

      <div style="border: 2px solid #7c0084; border-radius: 10px; padding: 30px; margin-bottom: 30px;">
        <div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Nome:</strong> ${ticket.user_name} ${ticket.user_last_name}</p>
        </div>

        ${
          ticket.user_cpf
            ? `<div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Documento CPF:</strong> ${this.maskCpf(ticket.user_cpf)}</p>
        </div>`
            : ticket.user_identity_card
              ? `<div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Documento Identidade (Internacional):</strong> ${ticket.user_identity_card}</p>
        </div>`
              : ""
        }

        <div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Ingresso:</strong> ${ticket.title}</p>
        </div>

        <div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Descrição:</strong> ${ticket.description || "N/A"}</p>
        </div>

        <div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Evento:</strong> ${ticket.event_title}</p>
        </div>

        <div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Data do evento:</strong> ${this.formatDate(ticket.event_start_date)}</p>
        </div>

        <div style="margin-bottom: 15px;">
          <p style="margin: 5px 0; color: #333;"><strong style="color: #7c0084;">Local do evento:</strong><br/>
          ${ticket.event_location.location_name} - ${ticket.event_location.street}, ${ticket.event_location.number}<br/>
          ${ticket.event_location.city_name}</p>
        </div>
      </div>

      ${
        qrCodeData
          ? `<div style="text-align: center; margin-top: 30px;">
        <img src="${qrCodeData}" alt="QR Code" style="width: 300px; height: 300px;" />
        <p style="color: #7c0084; margin-top: 10px; font-size: 14px;">Apresente este QR Code na entrada do evento</p>
      </div>`
          : ""
      }

      <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
        <p style="color: #666; font-size: 12px; margin: 5px 0;">Yes Seven Tickets - www.yes7tickets.com</p>
        <p style="color: #666; font-size: 12px; margin: 5px 0;">Ingresso gerado em: ${new Date().toLocaleDateString("pt-BR")}</p>
      </div>
    `;

    return container;
  }

  /**
   * Masks CPF for display (XXX.XXX.XXX-XX)
   */
  private maskCpf(cpf: string): string {
    if (!cpf) return "";
    const cleaned = cpf.replace(/\D/g, "");
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  /**
   * Formats date for display
   */
  private formatDate(date: string | Date): string {
    const dateObj = new Date(date);
    const dateStr = dateObj.toLocaleDateString("pt-BR");
    const timeStr = dateObj.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} ${timeStr}`;
  }
}
